import { Module, Mutation, Action } from "vuex-class-modules"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { projectModule } from "./project.module"
import {
  ITask,
  ModalTypesForCratingTask,
  PatchTaskList,
  ReturnTeamTask,
  Task,
  TaskDraftEdit,
  TaskType,
  TaskTypeOption,
  UpdateTaskList,
  UpdateTaskOrder
} from "@/models/Task"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import {
  AlertContentItem,
  AlertType,
  Item,
  ItemStatus,
  Status, StructuredDescription,
  TasksWithItemStatus,
  Team,
  UpdateObjectValue
} from "@/models"
import { AlertActions, alertModule } from "./alert.module"
import { $t } from "@/plugins/i18n"
import cloneDeep from "lodash/cloneDeep"
import isEqual from "lodash/isEqual"
import { ItemActions, itemModule } from "./item/item.module"
import { replaceHtmlTags, replaceHtmlTagsForEditor, sortTask } from "@/utils/helpers"
import { cutTextByNumberOfSymbols } from "@/helpers/cutTextByNumberOfSymbols"
import { sessionModule } from "./session.module"

export enum TaskActions {
  GET_TASKS = "GET_TASKS",
  GET_TASK = "GET_TASK",
  UPDATE_TASKS = "UPDATE_TASKS",
  UPDATE_TASKS_WITH_CLONE = "UPDATE_TASKS_WITH_CLONE",
  RESET_TASKS = "RESET_TASKS",
  CREATE_NEW_TASK = "CREATE_NEW_TASK",
  CHANGE_NEW_TASK = "CHANGE_NEW_TASK",
  TASKS_ORDER = "TASKS_ORDER",
  PATCH_TASKS = "PATCH_TASKS",
  ADD_TASK_IN_LIST = "ADD_TASK_IN_LIST",
  UPDATE_TASK_LIST_CLONE = "UPDATE_TASK_LIST_CLONE",
  GET_CLONE_TASK_LIST = "GET_CLONE_TASK_LIST",
  GET_TASK_DRAFT_EDITS = "GET_TASK_DRAFT_EDITS",
  TASK_LIST_WAS_EDITED = "TASK_LIST_WAS_EDITED",
  PUT_TASKS_WITH_CHANGES_CHECKING = "PUT_TASKS_WITH_CHANGES_CHECKING",
  REMOVE_TASK_FROM_LIST = "REMOVE_TASK_FROM_LIST",
  RESET_CHANGES_IN_TASK_LIST = "RESET_CHANGES_IN_TASK_LIST",
  GET_MAX_TASK_NAME_LENGTH = "GET_MAX_TASK_NAME_LENGTH",
  GET_TASKS_WITH_EMPTY_TASK = "GET_TASKS_WITH_EMPTY_TASK",
  SET_MODAL_TYPE_FOR_CREATING_NEW_TASK = "SET_MODAL_TYPE_FOR_CREATING_NEW_TASK",
  SET_MODAL_NEW_TASK_TYPE_OPTION = "SET_MODAL_NEW_TASK_TYPE_OPTION",
  GET_MODAL_NEW_TASK_TYPE_OPTION = "GET_MODAL_NEW_TASK_TYPE_OPTION",
  DELETE_TASK = "DELETE_TASK",
  RESTORE_TASK = "RESTORE_TASK",
  AVAILABLE_TASK_TYPES = "AVAILABLE_TASK_TYPES",
  PREPARE_RETURN_TEAM_TASK = "PREPARE_RETURN_TEAM_TASK",
  APPLY_TASKS_FROM_SSE = "APPLY_TASKS_FROM_SSE",
  RESTORE_TASK_DRAFT = "RESTORE_TASK_DRAFT"
}

const MAX_TASK_NAME_LENGTH = 74

const RESTORABLE_TASK_FIELDS: (keyof Task)[] = [
  "name", "type", "estimatedHours", "order", "owner", "ownerId", "fileMetadataIds",
  "structuredDescription", "spec", "referencedResourcePreviews", "expectedDeliveryDate",
  "estimationModificationTime", "outdatedEstimation", "oldEstimation", "checklistFullyChecked"
]

const getTaskSyncKey = (task?: Partial<Task>) => task?.localId || task?.id || ""

/**
 * Identifies a pending local task in a backend response before the task has received a server ID.
 */
const getTaskBackendMatchKey = (task: Partial<ITask>) => JSON.stringify([
  replaceHtmlTags(task.name ?? ""),
  task.order ?? null,
  task.typeParsed?.id ?? task.type ?? "",
])

/**
 * Matches a pending task to a response from an earlier save when only its title casing changed.
 * Order and type keep unrelated tasks with the same title separate.
 */
const getPendingTaskCaseMatchKey = (task: Partial<ITask>) => JSON.stringify([
  replaceHtmlTags(task.name ?? "").toLowerCase(),
  task.order ?? null,
  task.typeParsed?.id ?? task.type ?? "",
])

/**
 * Reuses local IDs for backend tasks so in-flight saves update their local counterparts instead of duplicating them.
 * Keeps the displayed owner photo URL when the owner and underlying picture have not changed.
 */
const hydrateTasksWithLocalIds = (
  currentTasks: Task[],
  nextTasks: ITask[],
  { matchPendingTasksByIdentity = true }: { matchPendingTasksByIdentity?: boolean } = {}
) => {
  const pendingTasks = currentTasks.filter(task => !task.id && (
    task.isNotEmpty ||
    replaceHtmlTagsForEditor(task.structuredDescription?.html ?? "") ||
    replaceHtmlTagsForEditor(task.spec?.html ?? "")
  ))
  const currentTasksById = new Map(
    currentTasks
      .filter(task => !!task.id)
      .map(task => [task.id as string, task] as const)
  )
  const pendingTasksByIdentity = matchPendingTasksByIdentity
    ? new Map(
      pendingTasks.map(task => [getTaskBackendMatchKey(task), task] as const)
    )
    : null
  const findUniquePendingCaseMatch = (task: ITask) => {
    const key = getPendingTaskCaseMatchKey(task)
    const pendingMatches = pendingTasks.filter(candidate => getPendingTaskCaseMatchKey(candidate) === key)
    return pendingMatches.length === 1 &&
      nextTasks.filter(candidate => getPendingTaskCaseMatchKey(candidate) === key).length === 1
      ? pendingMatches[0]
      : undefined
  }

  return nextTasks.map(task => {
    const backendMatchKey = getTaskBackendMatchKey(task)
    const matchedTask =
      (task.id ? currentTasksById.get(task.id) : undefined) ??
      pendingTasksByIdentity?.get(backendMatchKey) ??
      (matchPendingTasksByIdentity ? findUniquePendingCaseMatch(task) : undefined)
    const localId = matchedTask?.localId ?? task.localId

    if (matchedTask && !matchedTask.id) {
      pendingTasksByIdentity?.delete(getTaskBackendMatchKey(matchedTask))
    }

    const hydratedTask = Task.create({
      ...task,
      localId,
    })
    if (task.typeParsed) {
      hydratedTask.typeParsed = task.typeParsed
    }
    const previousOwner = matchedTask?.owner
    const nextOwner = hydratedTask.owner
    if (
      previousOwner?.id && nextOwner?.id === previousOwner.id &&
      nextOwner.picture?.pictureUrl === previousOwner.picture?.pictureUrl &&
      nextOwner.picture?.pictureStorageKey === previousOwner.picture?.pictureStorageKey
    ) {
      nextOwner.pictureUrl = previousOwner.pictureUrl
    }
    return hydratedTask
  })
}

/**
 * Applies backend fields only when they were not changed locally after the request snapshot was captured.
 */
const mergeTaskWithLocalChanges = (localTask: Task, requestTask: Task, backendTask: Task) => {
  const backendUpdates = Object.keys(backendTask).reduce((updates, field) => {
    const key = field as keyof Task
    if (isEqual(localTask[key], requestTask[key])) {
      updates[field] = cloneDeep(backendTask[key])
    }
    return updates
  }, {} as Record<string, unknown>)

  return Object.assign(cloneDeep(localTask), backendUpdates, { localId: localTask.localId })
}

@Module
class TaskModule extends BaseModule {
  tasks: Task[] = []
  task: Task
  cloneTaskList: Task[] = []
  modalTypeForCreatingTask: ModalTypesForCratingTask = null
  modalNewTaskTypeOption: TaskTypeOption | null = null

  get projectId() {
    return projectModule.currentProjectId
  }

  get [`get/${TaskActions.GET_TASKS_WITH_EMPTY_TASK}`]() {
    if (!this.tasks.length || this.tasks.at(-1)?.isNotEmpty) {
      const type = this.getDefaultTaskType()
      const newTask = new Task({ 
        order: this.getNewOrder(),
        type,
      })
      return [
        ...this.tasks,
        newTask,
      ]
    }
    return this.tasks
  }

  get [`get/${TaskActions.GET_TASKS}`]() {
    return this.tasks
  }

  get [`get/${TaskActions.GET_CLONE_TASK_LIST}`]() {
    return this.cloneTaskList
  }

  /** Saves only locally changed task fields, avoiding a second full task list in session storage. */
  get [`get/${TaskActions.GET_TASK_DRAFT_EDITS}`](): TaskDraftEdit[] {
    const baselineById = new Map(this.cloneTaskList.filter(task => !!task.id).map(task => [task.id, task] as const))
    const baselineByLocalId = new Map(this.cloneTaskList.map(task => [task.localId, task] as const))
    return this.tasks.flatMap((task): TaskDraftEdit[] => {
      const baseline = (task.id ? baselineById.get(task.id) : undefined) ?? baselineByLocalId.get(task.localId)
      if (!baseline) {
        return !task.id && task.isNotEmpty
          ? [{ localId: task.localId, fields: cloneDeep(task), isNew: true }]
          : []
      }
      const fieldNames = RESTORABLE_TASK_FIELDS.filter(field => !isEqual(task[field], baseline[field]))
      const fields = fieldNames.reduce((changes, field) => {
        Object.assign(changes, { [field]: cloneDeep(task[field]) })
        return changes
      }, {} as Partial<Task>)
      return fieldNames.length
        ? [{ taskId: task.id, localId: task.localId, fields, fieldNames }]
        : []
    })
  }

  get [`get/${TaskActions.GET_TASK}`]() {
    return this.task
  }

  get [`get/${TaskActions.GET_MAX_TASK_NAME_LENGTH}`]() {
    return MAX_TASK_NAME_LENGTH
  }

  get [`get/${TaskActions.TASK_LIST_WAS_EDITED}`]() {
    return !this.compareTaskLists(this.cloneTaskList.filter(task => !!task.name), this.tasks.filter(task => !!task.name))
  }

  get [`get/${TaskActions.SET_MODAL_TYPE_FOR_CREATING_NEW_TASK}`]() {
    return this.modalTypeForCreatingTask
  }

  get [`get/${TaskActions.GET_MODAL_NEW_TASK_TYPE_OPTION}`]() {
    return this.modalNewTaskTypeOption
  }

  get [`get/${TaskActions.AVAILABLE_TASK_TYPES}`]() {
    return this.getAvailableTaskTypes(true)
  }

  /** The task that reopens a Done Item returned to its team, typed by that team's config */
  @Action
  async [TaskActions.PREPARE_RETURN_TEAM_TASK]({
    item,
    team
  }: {
    item: Item
    team: Team
  }): Promise<ReturnTeamTask | null> {
    // the opened Item already has its tasks in the store
    let tasks = this.tasks
    if (item.id !== itemModule.item?.id) {
      const loadedTasks = await this[TaskActions.GET_TASKS]({
        itemId: item.id ?? "",
        projectId: item.projectId,
        onlyGet: true
      })
      if (!loadedTasks) return null
      tasks = new TasksWithItemStatus(loadedTasks).tasks
    }

    const returnedItem = Item.create(item)
    returnedItem.team = team
    returnedItem.teamType = team.type

    const taskTypes = this.getAvailableTaskTypes(true, returnedItem, tasks)
    const defaultType = this.getDefaultTaskType(returnedItem, tasks)
    const type = taskTypes.some(option => option.id === defaultType)
      ? defaultType
      : (taskTypes[0]?.id ?? defaultType)

    const task = new Task({
      name: "",
      type,
      itemId: item.id,
      order: (tasks.at(-1)?.order ?? 0) + 1,
      structuredDescription: new StructuredDescription()
    })
    return { item: returnedItem, task, taskTypes }
  }

  /** targetItem/targetTasks - an Item other than the opened one, e.g. a backlog row */
  getAvailableTaskTypes(filterDiscovery = false, targetItem?: Item, targetTasks?: Task[]): TaskTypeOption[] {
    const item = targetItem ?? itemModule.item
    if(!item) return [];
    const teamType = item.teamType ?? (item.isSupportCase ? 'NONE' : 'DEVELOPMENT');
    const itemConfig = sessionModule.itemConfig
    if(!itemConfig) return [];
    const selectedConfig = itemConfig[teamType];
    if(!selectedConfig) return [];
    let availableTaskTypes = selectedConfig.configs.find(c => 
      c.itemTypes.some(it => it === item.itemTypeParsed?.id)
    )?.taskTypes ?? [];
    if(filterDiscovery) {
      const hasDiscovery = (targetTasks ?? this.tasks).some(t => t.typeParsed.id === "DISCOVERY")
      availableTaskTypes = hasDiscovery ? availableTaskTypes.filter(opt => opt !== "DISCOVERY") : availableTaskTypes
    }
    return availableTaskTypes.map(e => Task.getParsedType(e));
  }

  getDefaultTaskType(targetItem?: Item, targetTasks?: Task[]): TaskType {
    const defaultType: TaskType = "DEV"
    const item = targetItem ?? itemModule.item
    if(!item) return defaultType;
    const itemConfig = sessionModule.itemConfig
    const itemTypeId = item.itemTypeParsed.id

    if (!itemConfig || !itemTypeId) return defaultType

    const teamType = item.teamType ?? (item.isSupportCase ? "NONE" : "DEVELOPMENT")
    const taskType =
      itemConfig[teamType]?.configs.find(c => c.itemTypes.includes(itemTypeId))?.defaultTaskType ??
      defaultType

    const hasDiscoveryTask = (targetTasks ?? this.tasks).some(t => t.typeParsed.id === "DISCOVERY")

    if (taskType === "DISCOVERY" && hasDiscoveryTask) {
      return this.getAvailableTaskTypes(true, targetItem, targetTasks)[0]?.id ?? defaultType
    }

    return taskType
  }


  compareTaskLists(firstTaskList, secondTaskList) {
    return isEqual(this.updatedSimpleDiffTasks(firstTaskList), this.updatedSimpleDiffTasks(secondTaskList))
  }

  updatedSimpleDiffTasks(arr: Task[]) {
    return arr.filter(task => task.name || task.id).map(task => task.getJsonObj())
  }

  getNewOrder() {
    const lastTask = this.tasks.at(-1)
    const lastTaskOrder = lastTask?.order ?? 0
    return lastTaskOrder + 1
  }

  @Mutation
  clearTasks() {
    this.tasks = []
  }

  /** Keeps saved task rows mounted when a full response, including a team change, replaces the list. */
  @Mutation
  setTasks(tasks: Task[]) {
    this.tasks = cloneDeep(hydrateTasksWithLocalIds(this.tasks, tasks, {
      matchPendingTasksByIdentity: false,
    })).sort(sortTask)
  }

  @Mutation
  setTaskListClone(tasks: Task[]) {
    this.cloneTaskList = cloneDeep(tasks).sort(sortTask)
  }

  @Mutation
  setTaskInList(task: Task) {
    this.tasks = [
      ...this.tasks,
      task,
    ].sort(sortTask)
  }

  @Mutation
  modifyTaskOrderAfterSetAllTasks() {
    if (!this.task) return
    Object.assign(this.task, { order: this.getNewOrder() })
  }

  @Mutation
  removeTaskFromList(removedTaskIndex: number) {
    this.tasks = this.tasks.filter((_, i) => i !== removedTaskIndex).map((task, index) => {
      task.order = index + 1
      return task
    })
    this.cloneTaskList = cloneDeep(this.tasks).sort(sortTask)
  }

  @Mutation
  createNewTask() {
    this.task = new Task({
      name: "",
      type: this.getDefaultTaskType(),
      order: this.getNewOrder(),
      structuredDescription: new StructuredDescription(),
    })
  }

  @Mutation
  changeNewTask(payload: UpdateObjectValue<Task>) {
    Object.assign(this.task, { [payload["id"]]: payload["value"] })
  }

  @Mutation
  setModalTypeForCreatingTask(payload: ModalTypesForCratingTask) {
    this.modalTypeForCreatingTask = payload
  }

  @Mutation
  setModalNewTaskTypeOption(taskTypeOption: TaskTypeOption | null) {
    this.modalNewTaskTypeOption = taskTypeOption
  }

  @Mutation
  applyTasksFromSSE(payload: { oldValue: Task[]; newValue: Task[] }) {
    const { oldValue, newValue } = payload
    const hydratedOldValue = hydrateTasksWithLocalIds(this.tasks, oldValue, { matchPendingTasksByIdentity: false })
    const hydratedNewValue = hydrateTasksWithLocalIds(this.tasks, newValue)

    const localMap = new Map(
      this.tasks
        .map(task => [getTaskSyncKey(task), task] as const)
        .filter(([taskKey]) => !!taskKey)
    )
    const oldMap = new Map(
      hydratedOldValue
        .map(task => [getTaskSyncKey(task), task] as const)
        .filter(([taskKey]) => !!taskKey)
    )

    const mergedTasks = hydratedNewValue.map(newTaskRaw => {
      const taskKey = getTaskSyncKey(newTaskRaw)
      const localTask = localMap.get(taskKey)
      const oldTask = oldMap.get(taskKey)

      if (!localTask || !oldTask) {
        return Task.create(newTaskRaw)
      }
      return mergeTaskWithLocalChanges(localTask, oldTask, newTaskRaw)
    })
    const taskListWasChanged = !this.compareTaskLists(this.cloneTaskList, this.tasks)
    const serverTaskKeys = new Set(
      [...hydratedOldValue, ...hydratedNewValue].map(task => getTaskSyncKey(task)).filter(Boolean)
    )
    const newServerTaskMatchKeys = new Set(hydratedNewValue.map(task => getTaskBackendMatchKey(task)))
    const newTasks = this.tasks.filter(task => {
      const hasTaskContent = task.isNotEmpty ||
        replaceHtmlTagsForEditor(task.structuredDescription.html) ||
        replaceHtmlTagsForEditor(task.spec?.html ?? "")
      const hasServerIdentityMatch = !task.id && newServerTaskMatchKeys.has(getTaskBackendMatchKey(task))
      return hasTaskContent && !serverTaskKeys.has(getTaskSyncKey(task)) && !hasServerIdentityMatch
    })

    this.tasks = [...mergedTasks, ...newTasks].sort(sortTask)

    if (!taskListWasChanged) {
      this.cloneTaskList = cloneDeep(this.tasks)
    } else {
      this.cloneTaskList = hydratedNewValue.map(task => Task.create(task)).sort(sortTask)
    }
  }


  @Action
  [TaskActions.APPLY_TASKS_FROM_SSE](payload: {
    oldValue: Task[],
    newValue: Task[]
  }) {
    this.applyTasksFromSSE(payload)
  }

  /** Restores only unsaved task fields over a fresh Item, keeping server changes made while the page was inactive. */
  @Action
  [TaskActions.RESTORE_TASK_DRAFT]({ localTasks, edits, serverTasks }: {
    localTasks: Task[]
    edits: TaskDraftEdit[]
    serverTasks: Task[]
  }) {
    const savedTasks = localTasks.map(task => Task.create(task))
    const freshTasks = hydrateTasksWithLocalIds(savedTasks, serverTasks)
    const editsById = new Map(edits.filter(edit => !!edit.taskId).map(edit => [edit.taskId, edit] as const))
    const mergedTasks = freshTasks.map(serverTask => {
      const edit = editsById.get(serverTask.id)
      const overrides = edit?.fieldNames?.reduce((fields, field) => {
        Object.assign(fields, { [field]: edit.fields[field] })
        return fields
      }, {} as Partial<Task>) ?? edit?.fields
      return edit
        ? Task.create({ ...serverTask, ...overrides, localId: edit.localId })
        : serverTask
    })
    const freshLocalIds = new Set(freshTasks.map(task => task.localId))
    const freshMatchKeys = new Set(freshTasks.map(task => getTaskBackendMatchKey(task)))
    const pendingTasks = edits.filter(edit => edit.isNew).map(edit => new Task(edit.fields)).filter(task =>
      !freshLocalIds.has(task.localId) && !freshMatchKeys.has(getTaskBackendMatchKey(task))
    )

    this.setTasks([...mergedTasks, ...pendingTasks])
    this.setTaskListClone(freshTasks)
    return this.tasks
  }

  @Action
  async [TaskActions.GET_TASKS]({ itemId, projectId, onlyGet } : {itemId: string, projectId?: string, onlyGet?: boolean}) {
    const data = await apiService.get<TasksWithItemStatus>(Endpoint.TASKS_LIST(projectId ?? this.projectId, itemId))
    return this.handleResponse<TasksWithItemStatus>(data, (data) => {
      if (!onlyGet) this.handleTasksResponse(data)
      return data
    })
  }

  /**
   * Persists drag-and-drop task ordering through the backend's dedicated order endpoint.
   */
  @Action
  async [TaskActions.TASKS_ORDER](payload: UpdateTaskOrder) {
    const { taskIdsAndOrder, itemId, projectId } = payload
    const data = await apiService.patch<TasksWithItemStatus>(
      Endpoint.TASKS_ORDER(projectId ?? this.projectId, itemId),
      taskIdsAndOrder
    )
    return this.handleResponse<TasksWithItemStatus>(data, (data) => {
      const tasksWithItemStatus = new TasksWithItemStatus(data)
      this.handleTasksResponse(tasksWithItemStatus)
      return tasksWithItemStatus
    })
  }

  @Action
  async [TaskActions.PATCH_TASKS](payload: PatchTaskList) {
    const { item, projectId, taskIdsAndEstimation } = payload
    const itemId = item?.id ?? ""
    const data = await apiService.patch<TasksWithItemStatus>(Endpoint.TASKS_ESTIMATION(projectId ?? this.projectId, itemId), taskIdsAndEstimation)
    return this.handleResponse<TasksWithItemStatus>(data, (data) => {
      const tasksWithItemStatus = new TasksWithItemStatus(data)
      const itemTitle = cutTextByNumberOfSymbols({
        text: item.title,
        numberOfSymbols: 16,
      })
      const alertContent: AlertContentItem[] = [
        {
          text: "Item ",
          type: "regular",
        },
        {
          text: `${itemTitle} `,
          type: "bold",
        },
        {
          text: $t("alert.updated"),
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
        uniqueType: `itemEstimation_${item.sequenceNumber}`,
      })
      itemModule[ItemActions.UPDATE_ITEM_STATUS_IN_LIST]({
        status: tasksWithItemStatus.status,
        itemId: item?.id ?? "",
      })
      return tasksWithItemStatus
    })
  }

  @Action
  async [TaskActions.RESET_TASKS]() {
    return new Promise((resolve) => {
      this.clearTasks()
      this.setTaskListClone(this.tasks)
      resolve(true)
    })
  }

  @Action
  [TaskActions.UPDATE_TASKS](tasks: Task[]) {
    this.setTasks(tasks)
  }

  @Action
  [TaskActions.ADD_TASK_IN_LIST](task: Task) {
    this.setTaskInList(task)
  }

  @Action
  async [TaskActions.CREATE_NEW_TASK]() {
    this.createNewTask()
  }

  @Action
  async [TaskActions.CHANGE_NEW_TASK](payload: UpdateObjectValue<Task>) {
    this.changeNewTask(payload)
  }

  @Action
  async [TaskActions.UPDATE_TASK_LIST_CLONE](tasks: Task[]) {
    this.setTaskListClone(tasks)
  }

  @Action
  async [TaskActions.RESET_CHANGES_IN_TASK_LIST]() {
    this.setTasks(this.cloneTaskList)
  }

  @Action
  async [TaskActions.UPDATE_TASKS_WITH_CLONE](tasks: Task[]) {
    this.setTasks(tasks)
    this.setTaskListClone(tasks)
  }

  @Action
  async [TaskActions.REMOVE_TASK_FROM_LIST](index: number) {
    this.removeTaskFromList(index)
  }

  @Action
  async [TaskActions.SET_MODAL_TYPE_FOR_CREATING_NEW_TASK](payload: ModalTypesForCratingTask) {
    this.setModalTypeForCreatingTask(payload)
  }

  @Action
  async [TaskActions.SET_MODAL_NEW_TASK_TYPE_OPTION](taskTypeOption: TaskTypeOption | null) {
    this.setModalNewTaskTypeOption(taskTypeOption)
  }

  @Action
  async [TaskActions.DELETE_TASK]({ task, itemId, projectId } : { task: Task, itemId: string, projectId?: string }) {
    const taskId = task.id ?? ""
    const data = await apiService.delete<TasksWithItemStatus>(Endpoint.DELETE_TASK(projectId ?? this.projectId, itemId, taskId))
    return this.handleResponse<TasksWithItemStatus>(data, (data) => {
      const tasksWithItemStatus = new TasksWithItemStatus(data)
      const taskTitle = cutTextByNumberOfSymbols({
        text: task.name,
        numberOfSymbols: 16,
      })

      const alertContent: AlertContentItem[] = [
        {
          text: "Task ",
          type: "regular",
        },
        {
          text: `${taskTitle} `,
          type: "bold",
        },
        {
          text: $t("alert.deleted"),
          type: "regular",
        },
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      this.handleTasksResponse(tasksWithItemStatus)
      return tasksWithItemStatus
    })
  }

  @Action
  async [TaskActions.RESTORE_TASK](
    { taskData, itemId, taskName, commentId }: { taskData: string; itemId: string; taskName: string; commentId: string}
  ) {
    const requestBody = {
      commentId,
      data: taskData
    };
  
    const response = await apiService.put<TasksWithItemStatus>(
      Endpoint.RESTORE_TASK(this.projectId, itemId),
      requestBody
      
    );
  
    return this.handleResponse<TasksWithItemStatus>(response, (data) => {
      const tasksWithItemStatus = new TasksWithItemStatus(data);
      const numberFromResponse = tasksWithItemStatus.sequenceNumber ?? "N/A";
      const taskTitle = ` was restored back into Item #${numberFromResponse}`
      const taskText =  `${taskName}`
      const alertContent: AlertContentItem[] = [
        {
          text: "Task ",
          type: "regular",
        },
        {
          text: `${taskText}`,
          type: "bold",
        },
        {
          text: `${taskTitle}`,
          type: "regular",
        }
      ]
      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
      })
      this.handleTasksResponse(tasksWithItemStatus);
      return tasksWithItemStatus;
    });
  }
  
  @Action
  async [TaskActions.PUT_TASKS_WITH_CHANGES_CHECKING](payload: UpdateTaskList) {
    const { tasks, item } = payload
    const savedTasks = this.cloneTaskList.filter((task): task is Task & { id: string } => !!task.id)
    const savedTasksByLocalId = new Map(savedTasks.map(task => [task.localId, task] as const))
    const savedTasksById = new Map(savedTasks.map(task => [task.id, task] as const))
    const findSavedTask = (task: Task) => savedTasksByLocalId.get(task.localId) ??
      (task.id ? savedTasksById.get(task.id) : undefined)
    const tasksWithServerIds = tasks.map(task => {
      const savedTask = findSavedTask(task)
      if (task.id || !savedTask?.id) return task
      return Object.assign(cloneDeep(task), { id: savedTask.id })
    })
    const requestTasks = cloneDeep(tasksWithServerIds)
    const editedTasks = tasksWithServerIds.filter(task => {
      if (!task.isNotEmpty) return false
      const savedTask = findSavedTask(task)
      return !savedTask || !isEqual(savedTask.getJsonObj(), task.getJsonObj())
    })

    if (!editedTasks.length) {
      const itemStatus = item.statusParsed?.id ?? "NOT_STARTED"
      return new TasksWithItemStatus({
        tasks: tasksWithServerIds,
        status: itemStatus as ItemStatus,
        estimation: Task.getItemEstimation(tasksWithServerIds),
        sequenceNumber: item.sequenceNumber,
      })
    }
    
    const itemId = item?.id ?? ""
    const projectId = payload.projectId ?? this.projectId
    const data = await apiService.put<TasksWithItemStatus>(Endpoint.TASKS_PROVIDED(projectId, itemId), editedTasks.map(task => task.getJsonObj()))
    return this.handleResponse<TasksWithItemStatus>(data, (data) => {
      const tasksWithItemStatus = new TasksWithItemStatus(data)
      let toastMessage = ""
      switch (tasksWithItemStatus.status) {
        case "RE_OPEN":
          toastMessage = "was Re-Opened and moved into Work in Progress."
          break
        default:
          toastMessage = $t("alert.updated")
          break
      }

      const itemTitle = cutTextByNumberOfSymbols({
        text: item.title,
        numberOfSymbols: 16,
      })

      const alertContent: AlertContentItem[] = [
        {
          text: "Item ",
          type: "regular",
        },
        {
          text: `${itemTitle} `,
          type: "bold",
        },
        {
          text: toastMessage,
          type: "regular",
        },
      ]

      alertModule[AlertActions.SHOW_ALERT]({
        content: alertContent,
        type: AlertType.SUCCESS,
        theme: "toast",
        uniqueType: "taskUpdates",
      })
      // a backlog row may save tasks while another Item or a new Item form is open
      if (item.id && itemModule.item?.id === item.id) {
        this.handleTasksResponse(tasksWithItemStatus, requestTasks)
      }
      return tasksWithItemStatus
    })
  }

  /**
   * Merges a backend response against the task snapshot that produced it, then retains newer local tasks.
   * The request snapshot prevents edits made while the request was in flight from breaking task identity matching.
   */
  handleTasksResponse(data: TasksWithItemStatus, requestTasks: Task[] = this.tasks) {
    const hydratedBackendTasks = hydrateTasksWithLocalIds(requestTasks, data.tasks)
    const requestTasksByLocalId = new Map(requestTasks.map(task => [task.localId, task] as const))
    const currentTasksByLocalId = new Map(this.tasks.map(task => [task.localId, task] as const))
    const backendTasks = hydratedBackendTasks.map(backendTask => {
      const requestTask = requestTasksByLocalId.get(backendTask.localId)
      const currentTask = currentTasksByLocalId.get(backendTask.localId)
      return requestTask && currentTask
        ? mergeTaskWithLocalChanges(currentTask, requestTask, backendTask)
        : backendTask
    })
    const backendTaskLocalIds = new Set(hydratedBackendTasks.map(task => task.localId))
    const backendTaskMatchKeys = new Set(hydratedBackendTasks.map(task => getTaskBackendMatchKey(task)))
    const pendingTasks = this.tasks.filter(task => {
      const hasTaskContent = task.isNotEmpty ||
        replaceHtmlTagsForEditor(task.structuredDescription?.html ?? "") ||
        replaceHtmlTagsForEditor(task.spec?.html ?? "")
      const wasUpdatedByResponse = backendTaskLocalIds.has(task.localId) ||
        backendTaskMatchKeys.has(getTaskBackendMatchKey(task))
      return !task.id && hasTaskContent && !wasUpdatedByResponse
    })
    const content = [...backendTasks, ...pendingTasks]
    this.setTasks(content)
    this.setTaskListClone(hydratedBackendTasks)
    this.modifyTaskOrderAfterSetAllTasks()
    const estimation = data.estimation ?? Task.getItemEstimation(content)
    const taskOwners = Item.getTaskOwners(content)
    itemModule[ItemActions.UPDATE_ITEM_VALUES]({
      payload: [
        {
          id: "status",
          value: data.status,
        },
        {
          id: "estimation",
          value: estimation,
        },
        {
          id: "statusParsed",
          value: Status.createById({
            id: data.status ?? "NOT_STARTED",
          }),
        },
        {
          id: "taskOwners",
          value: taskOwners,
        },
      ] as any,
      updateClone: true,
    })
  }
}

export const taskModule = new TaskModule({ store, name: Modules.TASK })
